// frontend/src/pages/Workbench.js
import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AuthContext } from '../context/AuthContext';

const API = process.env.REACT_APP_API_URL;

const MODELS = [
  { value: 'claude-sonnet-4-6',         label: 'Sonnet 4.6 (fast, default)' },
  { value: 'claude-opus-4-8',           label: 'Opus 4.8 (highest quality)' },
  { value: 'claude-opus-4-7',           label: 'Opus 4.7' },
  { value: 'claude-opus-4-6',           label: 'Opus 4.6' },
  { value: 'claude-haiku-4-5-20251001', label: 'Haiku 4.5 (cheapest)' }
];

const PRESETS = [
  { value: 'general',       label: 'General' },
  { value: 'bpmn-edit',     label: 'BPMN · Edit (round-trip)' },
  { value: 'bpmn-analysis', label: 'BPMN · Analyse' },
  { value: 'sop-build',     label: 'SOP · Build from BPMN' },
  { value: 'custom',        label: 'Custom (write your own)' }
];

// ── Style tokens ──
const C = {
  bg:        '#0d0f14',
  panel:     '#13161e',
  panel2:    '#1a1e2a',
  border:    '#2e3347',
  text:      '#e8e0d4',
  textDim:   '#9da0ad',
  textFaint: '#52586e',
  accent:    '#c97a32',  // burnt orange (Summit Track)
  accent2:   '#f5e0cc',
  success:   '#4caf7d',
  error:     '#e05555'
};

// ── Markdown renderers (dark theme) ──
const MD = {
  h1: ({ children }) => <h1 style={{ fontSize: '1.45rem', fontWeight: 600, margin: '20px 0 10px', color: C.accent2, lineHeight: 1.3 }}>{children}</h1>,
  h2: ({ children }) => <h2 style={{ fontSize: '1.2rem',  fontWeight: 600, margin: '18px 0 8px',  color: C.accent2, lineHeight: 1.3 }}>{children}</h2>,
  h3: ({ children }) => <h3 style={{ fontSize: '1.0rem',  fontWeight: 600, margin: '14px 0 6px',  color: C.text, lineHeight: 1.3 }}>{children}</h3>,
  h4: ({ children }) => <h4 style={{ fontSize: '0.92rem', fontWeight: 600, margin: '12px 0 4px',  color: C.text }}>{children}</h4>,
  p:  ({ children }) => <p style={{ margin: '8px 0', lineHeight: 1.6 }}>{children}</p>,
  ul: ({ children }) => <ul style={{ margin: '8px 0', paddingLeft: 24 }}>{children}</ul>,
  ol: ({ children }) => <ol style={{ margin: '8px 0', paddingLeft: 24 }}>{children}</ol>,
  li: ({ children }) => <li style={{ margin: '3px 0', lineHeight: 1.6 }}>{children}</li>,
  strong: ({ children }) => <strong style={{ fontWeight: 600, color: C.accent2 }}>{children}</strong>,
  em: ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
  a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: C.accent, textDecoration: 'underline' }}>{children}</a>,
  hr: () => <hr style={{ border: 'none', borderTop: `1px solid ${C.border}`, margin: '16px 0' }} />,
  blockquote: ({ children }) => <blockquote style={{ borderLeft: `3px solid ${C.accent}`, paddingLeft: 12, margin: '10px 0', color: C.textDim, fontStyle: 'italic' }}>{children}</blockquote>,
  code: ({ children }) => (
    <code style={{ background: C.panel2, padding: '1px 6px', borderRadius: 3, fontSize: '0.85em', fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', color: C.accent2 }}>
      {children}
    </code>
  ),
  table: ({ children }) => (
    <div style={{ overflowX: 'auto', margin: '12px 0' }}>
      <table style={{ borderCollapse: 'collapse', fontSize: '0.85em', minWidth: '100%' }}>{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead>{children}</thead>,
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => <tr>{children}</tr>,
  th: ({ children }) => <th style={{ border: `1px solid ${C.border}`, padding: '6px 10px', background: C.panel2, textAlign: 'left', fontWeight: 600 }}>{children}</th>,
  td: ({ children }) => <td style={{ border: `1px solid ${C.border}`, padding: '6px 10px', textAlign: 'left' }}>{children}</td>,
};

// ── Helpers ──
function fmtDate(d) {
  if (!d) return '';
  const date = new Date(d);
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  if (sameDay) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const yest = new Date(now); yest.setDate(yest.getDate() - 1);
  if (date.toDateString() === yest.toDateString()) return 'Yesterday';
  return date.toLocaleDateString([], { day: 'numeric', month: 'short' });
}

// Split assistant text into prose blocks and ```code``` blocks
function parseMessage(text) {
  if (!text) return [];
  const parts = [];
  const regex = /```(\w*)\n?([\s\S]*?)```/g;
  let lastIdx = 0;
  let m;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > lastIdx) parts.push({ type: 'text', body: text.slice(lastIdx, m.index) });
    parts.push({ type: 'code', lang: m[1] || '', body: m[2] });
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) parts.push({ type: 'text', body: text.slice(lastIdx) });
  return parts;
}

function CodeBlock({ lang, body }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(body).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  const download = () => {
    const ext = lang === 'xml' ? 'bpmn' : (lang || 'txt');
    const blob = new Blob([body], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `claude-output-${Date.now()}.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div style={{ background: C.panel2, border: `1px solid ${C.border}`, borderRadius: 4, margin: '10px 0', overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 12px', background: '#0d0f14', borderBottom: `1px solid ${C.border}`, fontSize: '0.7rem', color: C.textDim }}>
        <span>{lang || 'code'}</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={copy} style={{ background: 'none', border: 'none', color: copied ? C.success : C.textDim, cursor: 'pointer', fontSize: '0.7rem' }}>
            {copied ? '✓ copied' : 'copy'}
          </button>
          <button onClick={download} style={{ background: 'none', border: 'none', color: C.textDim, cursor: 'pointer', fontSize: '0.7rem' }}>
            download
          </button>
        </div>
      </div>
      <pre style={{ margin: 0, padding: 12, fontSize: '0.78rem', fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', overflowX: 'auto', color: C.text, whiteSpace: 'pre', lineHeight: 1.5 }}>
        {body}
      </pre>
    </div>
  );
}

function Message({ msg, msgIdx, conversationId, authHeader }) {
  const isUser = msg.role === 'user';
  const text = isUser ? (msg.displayText || msg.content) : (msg.displayText || msg.content);
  const parts = parseMessage(text);
  const [showThinking, setShowThinking] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadErr, setDownloadErr] = useState('');

  // ── Download assistant message as .docx (uses uploaded template if available) ──
  const downloadDocx = async () => {
    setDownloading(true);
    setDownloadErr('');
    try {
      const res = await fetch(
        `${API}/api/workbench/conversations/${conversationId}/messages/${msgIdx}/export`,
        { method: 'POST', headers: authHeader() }
      );
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') || '';
      const m = /filename="([^"]+)"/.exec(cd);
      const filename = m ? m[1] : `claude-output-${Date.now()}.docx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('docx download failed:', e);
      setDownloadErr(e.message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div style={{ marginBottom: 24, paddingBottom: 24, borderBottom: `1px solid ${C.border}`, textAlign: 'left' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: '0.6rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: isUser ? C.accent : C.accent2, fontWeight: 600 }}>
          {isUser ? 'You' : 'Claude'}
        </span>
        <span style={{ fontSize: '0.65rem', color: C.textFaint }}>{fmtDate(msg.timestamp)}</span>
        {msg.usage?.cache_read_input_tokens > 0 && (
          <span style={{ fontSize: '0.6rem', color: C.success }}>· {msg.usage.cache_read_input_tokens.toLocaleString()} cached</span>
        )}
        {!isUser && conversationId && (
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
            <button
              onClick={downloadDocx}
              disabled={downloading}
              title="Download as Word document (applies uploaded .docx template if found)"
              style={{
                background: 'none',
                border: `1px solid ${C.border}`,
                color: downloading ? C.textFaint : C.accent2,
                padding: '3px 10px',
                fontSize: '0.65rem',
                borderRadius: 2,
                cursor: downloading ? 'wait' : 'pointer',
                fontFamily: 'inherit'
              }}
            >
              {downloading ? 'Building…' : '⬇ .docx'}
            </button>
          </span>
        )}
      </div>

      {downloadErr && (
        <div style={{ marginBottom: 8, padding: 6, background: 'rgba(224,85,85,0.08)', border: `1px solid ${C.error}`, borderRadius: 2, fontSize: '0.7rem', color: C.error }}>
          Download failed: {downloadErr}
        </div>
      )}

      {msg.attachments && msg.attachments.length > 0 && (
        <div style={{ marginBottom: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {msg.attachments.map((a, i) => (
            <span key={i} style={{ fontSize: '0.7rem', padding: '3px 8px', background: C.panel2, border: `1px solid ${C.border}`, borderRadius: 2, color: C.textDim }}>
              📎 {a.name} <span style={{ color: C.textFaint }}>({Math.round((a.size || 0) / 1024)} KB)</span>
            </span>
          ))}
        </div>
      )}

      {msg.thinking && (
        <div style={{ marginBottom: 8 }}>
          <button onClick={() => setShowThinking(s => !s)} style={{ background: 'none', border: `1px solid ${C.border}`, color: C.textDim, padding: '3px 8px', fontSize: '0.65rem', borderRadius: 2, cursor: 'pointer' }}>
            {showThinking ? '▾ Hide thinking' : '▸ Show thinking'}
          </button>
          {showThinking && (
            <pre style={{ marginTop: 8, padding: 10, background: C.panel2, border: `1px solid ${C.border}`, borderRadius: 2, fontSize: '0.75rem', color: C.textDim, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
              {msg.thinking}
            </pre>
          )}
        </div>
      )}

      <div>
        {parts.map((p, i) =>
          p.type === 'code'
            ? <CodeBlock key={i} lang={p.lang} body={p.body} />
            : <div key={i} style={{ fontSize: '0.88rem', lineHeight: 1.6, color: C.text, textAlign: 'left' }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD}>{p.body}</ReactMarkdown>
              </div>
        )}
        {!text && <div style={{ color: C.textFaint, fontStyle: 'italic', fontSize: '0.85rem' }}>(empty)</div>}
      </div>
    </div>
  );
}

export default function Workbench() {
  const { user } = useContext(AuthContext);
  const token = localStorage.getItem('token');
  const authHeader = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [convs, setConvs] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null);   // full conversation w/ messages
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const [text, setText] = useState('');
  const [files, setFiles] = useState([]);       // [{ file, name, size }]
  const [renamingId, setRenamingId] = useState(null);
  const [renameVal, setRenameVal] = useState('');
  const [showSettings, setShowSettings] = useState(false);

  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  // ── Load conversation list on mount ──
  const loadList = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/workbench/conversations`, { headers: authHeader() });
      if (res.status === 404) { setError('access-denied'); return; }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setConvs(data);
    } catch (e) {
      console.error(e);
      setError(e.message);
    }
  }, [authHeader]);

  useEffect(() => { if (user) loadList(); }, [user, loadList]);

  // ── Load active conversation ──
  useEffect(() => {
    if (!activeId) { setActive(null); return; }
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/workbench/conversations/${activeId}`, { headers: authHeader() });
        if (res.ok) setActive(await res.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [activeId, authHeader]);

  // ── Scroll to bottom on new message ──
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [active?.messages?.length, sending]);

  // ── New conversation ──
  const newConv = async () => {
    const res = await fetch(`${API}/api/workbench/conversations`, {
      method: 'POST',
      headers: { ...authHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    });
    if (res.ok) {
      const created = await res.json();
      setConvs(c => [{ _id: created._id, title: created.title, preset: created.preset, model: created.model, updatedAt: created.updatedAt }, ...c]);
      setActiveId(created._id);
    }
  };

  // ── Delete ──
  const deleteConv = async (id) => {
    if (!window.confirm('Delete this conversation? This cannot be undone.')) return;
    await fetch(`${API}/api/workbench/conversations/${id}`, { method: 'DELETE', headers: authHeader() });
    setConvs(c => c.filter(x => x._id !== id));
    if (activeId === id) { setActiveId(null); setActive(null); }
  };

  // ── Rename ──
  const saveRename = async (id) => {
    if (!renameVal.trim()) { setRenamingId(null); return; }
    await fetch(`${API}/api/workbench/conversations/${id}`, {
      method: 'PATCH',
      headers: { ...authHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: renameVal.trim() })
    });
    setConvs(c => c.map(x => x._id === id ? { ...x, title: renameVal.trim() } : x));
    if (active?._id === id) setActive(a => ({ ...a, title: renameVal.trim() }));
    setRenamingId(null);
  };

  // ── Update settings ──
  const updateSettings = async (patch) => {
    if (!active) return;
    const res = await fetch(`${API}/api/workbench/conversations/${active._id}`, {
      method: 'PATCH',
      headers: { ...authHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(patch)
    });
    if (res.ok) {
      const updated = await res.json();
      setActive(updated);
      setConvs(c => c.map(x => x._id === updated._id ? { ...x, preset: updated.preset, model: updated.model } : x));
    }
  };

  // ── File handling ──
  const onFiles = (fileList) => {
    const arr = Array.from(fileList);
    setFiles(f => [...f, ...arr]);
  };
  const removeFile = (i) => setFiles(f => f.filter((_, idx) => idx !== i));

  // ── Send message ──
  const send = async () => {
    if (!active) return;
    if (!text.trim() && files.length === 0) return;
    if (sending) return;

    setSending(true);
    setError('');

    const fd = new FormData();
    fd.append('text', text);
    files.forEach(f => fd.append('files', f));

    // Optimistic UI: show pending user message immediately
    const tempUserMsg = {
      role: 'user',
      content: text,
      displayText: text,
      attachments: files.map(f => ({ name: f.name, mimeType: f.type, size: f.size })),
      timestamp: new Date().toISOString()
    };
    setActive(a => ({ ...a, messages: [...(a?.messages || []), tempUserMsg] }));
    setText('');
    setFiles([]);

    try {
      const res = await fetch(`${API}/api/workbench/conversations/${active._id}/message`, {
        method: 'POST',
        headers: authHeader(),
        body: fd
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Send failed');

      // Replace local state with authoritative server copy
      setActive(data.conversation);
      setConvs(c => {
        const newList = c.filter(x => x._id !== data.conversation._id);
        return [{
          _id: data.conversation._id,
          title: data.conversation.title,
          preset: data.conversation.preset,
          model: data.conversation.model,
          updatedAt: data.conversation.updatedAt
        }, ...newList];
      });
    } catch (e) {
      // Pop the optimistic message back off on error
      setActive(a => ({ ...a, messages: (a?.messages || []).slice(0, -1) }));
      setError(e.message);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      send();
    }
  };

  // ── Access denied screen ──
  if (error === 'access-denied') {
    return (
      <div style={{ background: C.bg, color: C.text, minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'IBM Plex Sans, sans-serif' }}>
        <div>Not found.</div>
      </div>
    );
  }

  // ── Render ──
  return (
    <div style={{ background: C.bg, color: C.text, minHeight: '88vh', display: 'flex', fontFamily: '"IBM Plex Sans", system-ui, sans-serif', borderRadius: 6, overflow: 'hidden', border: `1px solid ${C.border}`, textAlign: 'left' }}>

      {/* ── Sidebar ── */}
      <div style={{ width: 260, borderRight: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', background: C.panel }}>
        <div style={{ padding: 12, borderBottom: `1px solid ${C.border}` }}>
          <button onClick={newConv} style={{ width: '100%', padding: '8px 12px', background: C.accent, color: 'white', border: 'none', borderRadius: 3, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
            + New conversation
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          {convs.length === 0 && (
            <div style={{ padding: 20, color: C.textFaint, fontSize: '0.75rem', textAlign: 'center' }}>No conversations yet.</div>
          )}
          {convs.map(c => (
            <div
              key={c._id}
              onClick={() => activeId !== c._id && setActiveId(c._id)}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                background: c._id === activeId ? C.panel2 : 'transparent',
                borderLeft: c._id === activeId ? `2px solid ${C.accent}` : '2px solid transparent',
                fontSize: '0.8rem'
              }}
            >
              {renamingId === c._id ? (
                <input
                  autoFocus
                  value={renameVal}
                  onChange={e => setRenameVal(e.target.value)}
                  onBlur={() => saveRename(c._id)}
                  onKeyDown={e => { if (e.key === 'Enter') saveRename(c._id); if (e.key === 'Escape') setRenamingId(null); }}
                  style={{ width: '100%', background: C.bg, color: C.text, border: `1px solid ${C.accent}`, padding: '3px 6px', fontSize: '0.8rem', fontFamily: 'inherit', borderRadius: 2, outline: 'none' }}
                />
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{c.title}</span>
                    <div style={{ display: 'flex', gap: 4, opacity: 0.5 }}>
                      <button title="Rename" onClick={e => { e.stopPropagation(); setRenamingId(c._id); setRenameVal(c.title); }} style={{ background: 'none', border: 'none', color: C.textDim, cursor: 'pointer', padding: 2, fontSize: '0.75rem' }}>✎</button>
                      <button title="Delete" onClick={e => { e.stopPropagation(); deleteConv(c._id); }} style={{ background: 'none', border: 'none', color: C.textDim, cursor: 'pointer', padding: 2, fontSize: '0.75rem' }}>✕</button>
                    </div>
                  </div>
                  <div style={{ fontSize: '0.65rem', color: C.textFaint, marginTop: 2 }}>
                    {PRESETS.find(p => p.value === c.preset)?.label || c.preset} · {fmtDate(c.updatedAt)}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
        <div style={{ padding: 10, borderTop: `1px solid ${C.border}`, fontSize: '0.65rem', color: C.textFaint }}>
          {user?.username} · Workbench
        </div>
      </div>

      {/* ── Main panel ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {!active && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', color: C.textDim, fontSize: '0.85rem', gap: 12 }}>
            <div>{loading ? 'Loading…' : 'Select or start a conversation.'}</div>
          </div>
        )}

        {active && (
          <>
            {/* Top bar */}
            <div style={{ padding: '10px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', background: C.panel }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 500, flex: 1, minWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{active.title}</div>
              <select
                value={active.preset}
                onChange={e => {
                  if (e.target.value === 'custom') setShowSettings(true);
                  updateSettings({ preset: e.target.value });
                }}
                style={{ background: C.panel2, color: C.text, border: `1px solid ${C.border}`, padding: '4px 8px', fontSize: '0.72rem', borderRadius: 2, fontFamily: 'inherit', outline: 'none' }}
              >
                {PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
              <button onClick={() => setShowSettings(s => !s)} style={{ background: 'none', border: `1px solid ${C.border}`, color: C.textDim, padding: '4px 10px', fontSize: '0.7rem', cursor: 'pointer', borderRadius: 2 }}>
                {showSettings ? '✕' : '⚙'} Settings
              </button>
            </div>

            {showSettings && (
              <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}`, background: C.panel2, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', fontSize: '0.75rem' }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ color: C.textDim, fontSize: '0.65rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Model</span>
                  <select value={active.model} onChange={e => updateSettings({ model: e.target.value })} style={{ background: C.bg, color: C.text, border: `1px solid ${C.border}`, padding: '4px 8px', fontSize: '0.75rem', fontFamily: 'inherit', borderRadius: 2 }}>
                    {MODELS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ color: C.textDim, fontSize: '0.65rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Max output tokens</span>
                  <input type="number" min="1000" max="128000" step="1000" defaultValue={active.maxTokens} onBlur={e => updateSettings({ maxTokens: parseInt(e.target.value) || 32000 })} style={{ background: C.bg, color: C.text, border: `1px solid ${C.border}`, padding: '4px 8px', fontSize: '0.75rem', fontFamily: 'inherit', borderRadius: 2, width: 100 }} />
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="checkbox" checked={active.thinkingEnabled} onChange={e => updateSettings({ thinkingEnabled: e.target.checked })} />
                  <span>Extended thinking</span>
                </label>
                {active.thinkingEnabled && (
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ color: C.textDim, fontSize: '0.65rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Thinking budget</span>
                    <input type="number" min="1024" step="1000" defaultValue={active.thinkingBudget} onBlur={e => updateSettings({ thinkingBudget: parseInt(e.target.value) || 5000 })} style={{ background: C.bg, color: C.text, border: `1px solid ${C.border}`, padding: '4px 8px', fontSize: '0.75rem', fontFamily: 'inherit', borderRadius: 2, width: 100 }} />
                  </label>
                )}
                {active.preset === 'custom' && (
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flexBasis: '100%', width: '100%' }}>
                    <span style={{ color: C.textDim, fontSize: '0.65rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                      Custom system prompt <span style={{ textTransform: 'none', color: C.textFaint, letterSpacing: 0 }}>(saves when you click out)</span>
                    </span>
                    <textarea
                      defaultValue={active.customSystemPrompt || ''}
                      onBlur={e => updateSettings({ customSystemPrompt: e.target.value })}
                      placeholder={'Define how Claude should behave for this conversation. E.g.:\n\n"You are an analyst reviewing loan applications. Be concise. Cite policy section numbers when relevant. Use Australian English."'}
                      rows={6}
                      style={{ background: C.bg, color: C.text, border: `1px solid ${C.border}`, padding: 8, fontSize: '0.78rem', fontFamily: 'inherit', borderRadius: 2, outline: 'none', resize: 'vertical', width: '100%', boxSizing: 'border-box', lineHeight: 1.5 }}
                    />
                  </label>
                )}
              </div>
            )}

            {/* Messages */}
            <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
              {(active.messages || []).map((m, i) => (
                <Message
                  key={i}
                  msg={m}
                  msgIdx={i}
                  conversationId={active._id}
                  authHeader={authHeader}
                />
              ))}
              {sending && (
                <div style={{ color: C.accent2, fontSize: '0.8rem', fontStyle: 'italic', padding: '12px 0' }}>
                  Claude is thinking…
                </div>
              )}
              {error && !sending && (
                <div style={{ color: C.error, fontSize: '0.78rem', padding: 12, background: 'rgba(224,85,85,0.08)', border: `1px solid ${C.error}`, borderRadius: 3, marginTop: 8 }}>
                  Error: {error}
                </div>
              )}
            </div>

            {/* Input */}
            <div style={{ borderTop: `1px solid ${C.border}`, padding: 12, background: C.panel }}>
              {files.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                  {files.map((f, i) => (
                    <span key={i} style={{ fontSize: '0.72rem', padding: '4px 10px', background: C.panel2, border: `1px solid ${C.border}`, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                      📎 {f.name} <span style={{ color: C.textFaint }}>({Math.round(f.size / 1024)} KB)</span>
                      <button onClick={() => removeFile(i)} style={{ background: 'none', border: 'none', color: C.textFaint, cursor: 'pointer', fontSize: '0.8rem' }}>✕</button>
                    </span>
                  ))}
                </div>
              )}
              <div
                style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); onFiles(e.dataTransfer.files); }}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  multiple
                  style={{ display: 'none' }}
                  onChange={e => { onFiles(e.target.files); e.target.value = ''; }}
                />
                <button onClick={() => fileInputRef.current?.click()} title="Attach files" style={{ background: 'none', border: `1px solid ${C.border}`, color: C.textDim, padding: '8px 12px', cursor: 'pointer', borderRadius: 2, fontSize: '0.85rem' }}>📎</button>
                <textarea
                  value={text}
                  onChange={e => setText(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder="Message Claude…  (⌘/Ctrl + Enter to send.  Drop files anywhere on this row.)"
                  rows={3}
                  style={{ flex: 1, background: C.bg, color: C.text, border: `1px solid ${C.border}`, padding: 10, fontSize: '0.85rem', fontFamily: 'inherit', borderRadius: 2, outline: 'none', resize: 'vertical' }}
                />
                <button
                  onClick={send}
                  disabled={sending || (!text.trim() && files.length === 0)}
                  style={{
                    background: sending ? C.textFaint : C.accent,
                    color: 'white', border: 'none', padding: '10px 18px',
                    cursor: sending ? 'not-allowed' : 'pointer',
                    borderRadius: 2, fontSize: '0.8rem', fontWeight: 600,
                    opacity: (!text.trim() && files.length === 0) ? 0.5 : 1
                  }}
                >
                  {sending ? '…' : 'Send'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}